import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { useAccount, useMsal } from '@azure/msal-react'
import { parse, stringify } from 'query-string'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5'

import MenuBar from './components/MenuBar'
import AppCTX from './context/AppContext'
import useWindowWidthLogging from './hooks/useWindowWidthLogging'
import { getAccessKey, setAccessKey } from './lib/accessKey'
import type { SignInUser } from './lib/network'
import {
  aquireTokenSilently,
  hasCustomerBeenOnboarded,
  resolveFullCustomer,
} from './lib/network'
import { useSignalR } from './lib/signalR/hooks/useSignalR'
import logger from './lib/vouchLogger'
import { ThemeProviders } from './theme'
import type { IAccountInfo } from './types/Msal'
import type { Squad } from './utils/squad'
import AccountantRefereePage from './views/AccountantReferee'
import ApplicantPage from './views/Applicant'
import type { HandleLoginArgs } from './views/Auth'
import Auth from './views/Auth'
import ChangeLogPage from './views/ChangeLog'
import DashboardLoad from './views/DashboardLoad'
import EmployerRefereePage from './views/EmployerReferee'
import ExecutivePage from './views/Executive'
import ExecutivesPage from './views/Executives'
import FraudPage from './views/Fraud'
import GoToNextApplicant from './views/GoToNextApplicant'
import HandleInvite from './views/HandleInvite'
import HomePage from './views/Home'
import InviteError from './views/InviteError'
import LandlordRefereePage from './views/LandlordReferee'
import Onboarding from './views/Onboarding'
import SettingsPage from './views/Settings'
import SquadPage from './views/Squad'
import SquadsPage from './views/Squads'

const AppContainer = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: min-content 1fr;
  align-items: stretch;
`

export const PanelContainer = styled.div`
  // By default, a grid item cannot be smaller than the size of its content.
  // Grid items have an initial size of min-width: auto and min-height: auto.
  // You can override this behavior by setting grid items to min-width: 0,
  // min-height: 0 or overflow with any value other than visible.
  min-height: 0;
`

type AppSessionState =
  | { status: 'initializing' }
  | { status: 'unauthenticated' }
  | { status: 'bootstrapping' }
  | { status: 'redirecting' }
  | { status: 'onboarding' }
  | { status: 'error' }
  | {
      status: 'ready'
      token: string
      settings: SignInUser
      customerId: string | undefined
      squads: Squad[]
      accessKey: string
    }

const isInteractiveMsalStatus = (inProgress: InteractionStatus) =>
  inProgress === InteractionStatus.Startup ||
  inProgress === InteractionStatus.Login ||
  inProgress === InteractionStatus.Logout ||
  inProgress === InteractionStatus.HandleRedirect

const getPendingInviteId = () => {
  const localStr = localStorage.getItem('vchLogin') || ''

  if (!localStr || localStr.trim() === '') {
    return ''
  }

  try {
    const storage = JSON.parse(localStr)
    if (storage.invId && storage.status === 0) {
      localStorage.removeItem('vchLogin')
      return storage.invId as string
    }
  } catch {
    localStorage.removeItem('vchLogin')
  }

  return ''
}

const AuthenticatedApp = ({
  session,
  account,
  collapsedMenu,
  handleLogOut,
  onChangeCustomerId,
  onUpdateExecutive,
  setCollapsedMenu,
}: {
  session: Extract<AppSessionState, { status: 'ready' }>
  account: ReturnType<typeof useAccount>
  collapsedMenu: boolean
  handleLogOut: () => void
  onChangeCustomerId: (customerId: string) => void
  onUpdateExecutive: ({ squads }: { squads: Squad[] }) => void
  setCollapsedMenu: Dispatch<SetStateAction<boolean>>
}) => (
  <AppCTX.Provider
    value={{
      token: session.token,
      personId: session.settings?.id || '',
      personEmail: account?.username ?? '',
      customerId: session.customerId,
      workspaceSettings: session.settings?.workspaceSettings ?? null,
      squads: session.squads,
      accessKey: session.accessKey,
      skillLevel: session.settings ? (session.settings.skillLevel ?? 1) : 1,
    }}
  >
    <AppContainer>
      <PanelContainer className="menu-drawer">
        <MenuBar
          userData={session.settings}
          setCollapsedMenu={setCollapsedMenu}
          collapsedMenu={collapsedMenu}
          handleLogOut={handleLogOut}
        />
      </PanelContainer>
      <PanelContainer>
        <QueryParamProvider
          adapter={ReactRouter5Adapter}
          options={{
            searchStringToObject: parse,
            objectToSearchString: stringify,
          }}
        >
          <Switch>
            <Route exact path="/">
              {window.location.href.includes('?inviteId=') ? (
                <HandleInvite />
              ) : (
                <Redirect to="/applicants" />
              )}
            </Route>
            <Route path={['/applicants/:encodedParams', '/activity/:encodedParams']}>
              <HomePage
                userData={session.settings}
                token={session.token}
                setCollapsedMenu={setCollapsedMenu}
              />
            </Route>
            <Route path="/applicant/:subjectId/change-log" exact>
              <ChangeLogPage />
            </Route>
            <Route path="/applicant/:subjectId" exact>
              <ApplicantPage />
            </Route>
            <Route path="/employer-referee/:employerId" exact>
              <EmployerRefereePage />
            </Route>
            <Route path="/accountant-referee/:accountantId" exact>
              <AccountantRefereePage />
            </Route>
            <Route path="/landlord-referee/:subjectId" exact>
              <LandlordRefereePage />
            </Route>
            <Route path={['/applicants', '/activity']}>
              <HomePage
                userData={session.settings}
                token={session.token}
                setCollapsedMenu={setCollapsedMenu}
              />
            </Route>
            <Route path="/fraud">
              <FraudPage />
            </Route>
            <Route path="/next-applicant">
              <GoToNextApplicant />
            </Route>
            <Route path="/executives" exact>
              <ExecutivesPage collapsedMenu={collapsedMenu} />
            </Route>
            <Route path="/executive/:userId" exact>
              <ExecutivePage
                collapsedMenu={collapsedMenu}
                onUpdateExecutive={onUpdateExecutive}
              />
            </Route>
            <Route path="/squad/:squad" exact>
              <SquadPage collapsedMenu={collapsedMenu} />
            </Route>
            <Route path="/squads" exact>
              <SquadsPage collapsedMenu={collapsedMenu} />
            </Route>
            <Route path="/profile" exact>
              <SettingsPage
                collapsedMenu={collapsedMenu}
                userData={session.settings}
                onChangeCustomerId={onChangeCustomerId}
              />
            </Route>
            <ReactTooltip place="top" type="light" effect="solid" id="main" />
          </Switch>
        </QueryParamProvider>
      </PanelContainer>
    </AppContainer>
  </AppCTX.Provider>
)

export const AppSession = () => {
  const { accounts, instance, inProgress } = useMsal()
  const account = useAccount(accounts[0] || {})
  const [sessionState, setSessionState] = useState<AppSessionState>({
    status: 'initializing',
  })
  const [collapsedMenu, setCollapsedMenu] = useState(false)
  const bootstrapAbortRef = useRef<AbortController | null>(null)
  const redirectStartedRef = useRef(false)

  useSignalR({
    userId: sessionState.status === 'ready' ? sessionState.settings?.id : undefined,
    customerId: sessionState.status === 'ready' ? sessionState.customerId : undefined,
  })

  useWindowWidthLogging()

  useEffect(() => {
    return () => {
      bootstrapAbortRef.current?.abort()
    }
  }, [account])

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return
    }

    if (bootstrapAbortRef.current && !bootstrapAbortRef.current.signal.aborted) {
      return
    }

    const controller = new AbortController()
    bootstrapAbortRef.current = controller
    const { signal } = controller

    if (!account) {
      redirectStartedRef.current = false
      setAccessKey('')
      setSessionState({ status: 'unauthenticated' })
      return
    }

    setAccessKey('')
    setSessionState({ status: 'bootstrapping' })

    const bootstrapSession = async () => {
      try {
        const tokenRes = await aquireTokenSilently(instance, account)

        if (signal.aborted) {
          return
        }

        if (!tokenRes?.accessToken) {
          setAccessKey('')
          setSessionState({ status: 'error' })
          return
        }

        const { idTokenClaims }: IAccountInfo = account
        const invId = getPendingInviteId()

        const signIn = await hasCustomerBeenOnboarded(
          tokenRes.accessToken,
          invId,
          idTokenClaims.oid,
        )

        if (signal.aborted) {
          return
        }

        switch (signIn.status) {
          case 200:
            setAccessKey(signIn.accessKey)
            setSessionState({
              status: 'ready',
              token: tokenRes.accessToken,
              settings: signIn.settings,
              customerId: signIn.customerId,
              squads: signIn.squads,
              accessKey: signIn.accessKey,
            })
            break
          case 204:
            setAccessKey('')
            setSessionState({ status: 'onboarding' })
            break
          case 410:
          default:
            setAccessKey('')
            setSessionState({ status: 'error' })
            break
        }
      } catch (error) {
        if (signal.aborted) {
          return
        }

        if (error instanceof InteractionRequiredAuthError) {
          if (redirectStartedRef.current) {
            return
          }

          redirectStartedRef.current = true
          setSessionState({ status: 'redirecting' })

          try {
            await instance.acquireTokenRedirect({
              account,
              scopes: [`${import.meta.env.VITE_B2C_APP_ID}`],
            })
          } catch {
            redirectStartedRef.current = false
            if (!signal.aborted) {
              setAccessKey('')
              setSessionState({ status: 'error' })
            }
          }

          return
        }

        setAccessKey('')
        setSessionState({ status: 'error' })
      }
    }

    void bootstrapSession()
  }, [account, inProgress, instance])

  const readyCustomerId =
    sessionState.status === 'ready' ? sessionState.customerId : undefined
  const readyToken = sessionState.status === 'ready' ? sessionState.token : undefined

  useEffect(() => {
    if (!readyCustomerId || !readyToken) {
      return
    }

    const currentAccessKey = getAccessKey()
    if (!currentAccessKey) {
      return
    }

    const requestedCustomerId = readyCustomerId
    const requestedToken = readyToken

    resolveFullCustomer({
      token: requestedToken,
      customerId: requestedCustomerId,
      accessKey: currentAccessKey,
    })
      .then((res: Response) => {
        if (!res.ok) {
          throw new Error(`resolveFullCustomer failed with status ${res.status}`)
        }

        return res.json()
      })
      .then(data => {
        if (!data.config) {
          return
        }

        setSessionState(previous => {
          if (
            previous.status !== 'ready' ||
            previous.customerId !== requestedCustomerId ||
            previous.token !== requestedToken
          ) {
            return previous
          }

          return {
            ...previous,
            settings: {
              ...previous.settings,
              config: data.config,
            },
          }
        })
      })
      .catch((error: unknown) => {
        logger.error('Failed to resolve full customer', { error })
      })
  }, [readyCustomerId, readyToken])

  const handleLogOut = () => {
    bootstrapAbortRef.current?.abort()
    setAccessKey('')
    setSessionState({ status: 'unauthenticated' })
    if (!accounts || accounts.length < 1) {
      return
    }
    instance.logout()
  }

  const handleLogin = async (state: HandleLoginArgs) => {
    let options: { state: string; scopes: string[] } = { state: '', scopes: [] }

    if (state) {
      Object.entries(state).forEach(([key, value]) => {
        // eslint-disable-next-line eqeqeq
        if (`${key}` == 'inviteCode') {
          localStorage.setItem('vchLogin', JSON.stringify({ invId: value, status: 0 }))
        }
      })

      const joinedState = Object.values(state).join('|') || ''
      options = {
        state: `${joinedState}`,
        scopes: [`${import.meta.env.VITE_B2C_APP_ID}`],
      }
    }

    await instance.loginRedirect(options)
  }

  const handleChangeCustomerId = (customerId: string) => {
    setSessionState(previous =>
      previous.status === 'ready' ? { ...previous, customerId } : previous,
    )
  }

  const handleUpdateExecutive = ({ squads }: { squads: Squad[] }) => {
    setSessionState(previous =>
      previous.status === 'ready' ? { ...previous, squads } : previous,
    )
  }

  if (
    sessionState.status === 'initializing' ||
    sessionState.status === 'bootstrapping' ||
    sessionState.status === 'redirecting' ||
    (sessionState.status === 'ready' && isInteractiveMsalStatus(inProgress))
  ) {
    return <DashboardLoad />
  }

  if (sessionState.status === 'ready' && !account) {
    return <Auth handleLogin={handleLogin} />
  }

  if (sessionState.status === 'error') {
    return <InviteError handleLogOut={handleLogOut} />
  }

  if (sessionState.status === 'onboarding') {
    return (
      <Onboarding
        handleLogOut={handleLogOut}
        loggedInAccount={account}
        username={account?.username}
      />
    )
  }

  if (sessionState.status === 'unauthenticated') {
    return <Auth handleLogin={handleLogin} />
  }

  return (
    <AuthenticatedApp
      session={sessionState}
      account={account}
      collapsedMenu={collapsedMenu}
      handleLogOut={handleLogOut}
      onChangeCustomerId={handleChangeCustomerId}
      onUpdateExecutive={handleUpdateExecutive}
      setCollapsedMenu={setCollapsedMenu}
    />
  )
}

const App = () => (
  <ThemeProviders>
    <Router>
      <AppSession />
    </Router>
  </ThemeProviders>
)

export default App
